import { forwardRef, MutableRefObject, useState } from "react";
import BaseLoading from "../loading/BaseLoading";
import { IButtonProps } from "../../properties/ButtonProperties";
import { useGlowOrb } from "./useGlowOrb";

// PrimaryButton — the product's primary CTA: a frosted-glass pill with a
// mouse-following aqua glow orb (this WAS the "start" treatment; it has been
// promoted to be THE primary button — the solid-teal look is retired).
// forwardRef is kept so callers can focus / programmatically click it
// (login & forgot-password Enter-to-submit); the forwarded ref is merged with
// the glow orb's own internal ref via a callback ref.
const PrimaryButton = forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      title,
      size,
      onClick,
      disabled,
      block,
      fixedWidth,
    },
    forwardedRef
  ) => {
    const [loading, setLoading] = useState(false);
    const { ref: orbRef, handlers, orb } = useGlowOrb();

    // The glow orb needs the node for its pointer math; callers may also pass a
    // ref. Fan the node out to both.
    const setRefs = (node: HTMLButtonElement | null) => {
      (orbRef as MutableRefObject<HTMLButtonElement | null>).current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        (forwardedRef as MutableRefObject<HTMLButtonElement | null>).current =
          node;
      }
    };

    const internalOnClick = async (e: any) => {
      e.stopPropagation();
      if (loading) return;
      if (disabled) return;
      setLoading(true);
      await onClick();
      setLoading(false);
    };

    let currentHeightClassName = "";
    let currentLoadingSize;
    if (size === "small") {
      currentHeightClassName = "btn-h-8";
      currentLoadingSize = 14;
    } else if (size === "medium") {
      currentHeightClassName = "btn-h-10";
      currentLoadingSize = 16;
    } else if (size === "large") {
      currentHeightClassName = "btn-h-12";
      currentLoadingSize = 18;
    } else if (size === "unlimited") {
      currentHeightClassName = "btn-h-8";
      currentLoadingSize = 14;
    } else if (size === "compact") {
      currentHeightClassName = "btn-h-8";
      currentLoadingSize = 14;
    }

    return (
      <button
        ref={setRefs}
        className={`btn-base btn-start ${
          loading ? "btn-loading" : disabled ? "btn-disabled" : ""
        } ${block ? "btn-block" : fixedWidth ? "btn-w" : ""} ${currentHeightClassName}`}
        disabled={disabled || loading}
        onClick={internalOnClick}
        {...handlers}
      >
        {orb}
        {loading ? (
          <BaseLoading
            height={currentLoadingSize}
            width={currentLoadingSize}
            primaryColor="#0f899c"
            secondaryColor="#0f899c"
          />
        ) : (
          <div className="btn-content">
            <span>{title}</span>
          </div>
        )}
      </button>
    );
  }
);

PrimaryButton.displayName = "PrimaryButton";

export default PrimaryButton;
